import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useUpdateOrganization } from "@/services/organizations.services";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import { Center } from "@/common/css";
import { FormProvider, useForm } from "react-hook-form";
import UploadFileInput from "@/common/input/UploadFileInput";
import { LP_FE_BASE_URL } from "@/utils/constant/globalEnv.constant";

interface UpdateOrganizationModalProps {
  organization: {
    id: number;
    name: string;
    slug: string;
    description?: string;
    org_type?: string;
    sub_title?: string;
    category_id?: number;
    employee_count_min?: number;
    employee_count_max?: number;
    address?: string;
    website_url?: string;
    ph_no?: string;
    linkedin_url?: string;
    logo_url?: string;
    banner_url?: string;
    [key: string]: any;
  };
  onClose: () => void;
  afterCall?: () => void;
}

const UpdateOrganizationModal: React.FC<UpdateOrganizationModalProps> = ({ organization, onClose, afterCall }) => {
  // Helper function to extract only digits from phone number
  const extractPhoneDigits = (ph_no?: string): string => {
    if (!ph_no) return "";
    // Extract only digits from the phone number string
    return ph_no.replace(/\D/g, "");
  };

  const [formData, setFormData] = useState({
    name: organization.name || "",
    slug: organization.slug || "",
    description: organization.description || "",
    org_type: organization.org_type || "ORG",
    sub_title: organization.sub_title || "",
    category_id: organization.category_id || (organization.org_type === "EDU" ? 1 : 2),
    employee_count_min: organization.employee_count_min?.toString() || "",
    employee_count_max: organization.employee_count_max?.toString() || "",
      address: organization.address || "",
      website_url: organization.website_url || "",
      ph_no: extractPhoneDigits(organization.ph_no),
      linkedin_url: organization.linkedin_url || "",
    logo_url: organization.logo_url || "",
    banner_url: organization.banner_url || "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const updateMutation = useUpdateOrganization();
  
  // Create a form context for UploadFileInput components
  const uploadFormMethods = useForm({
    defaultValues: {
      logo_url: organization.logo_url 
        ? { value: organization.logo_url, previewUrl: organization.logo_url.startsWith("http") ? organization.logo_url : "" }
        : { value: "", previewUrl: "" },
      banner_url: organization.banner_url
        ? { value: organization.banner_url, previewUrl: organization.banner_url.startsWith("http") ? organization.banner_url : "" }
        : { value: "", previewUrl: "" },
    },
  });

  useEffect(() => {
    setFormData({
      name: organization.name || "",
      slug: organization.slug || "",
      description: organization.description || "",
      org_type: organization.org_type || "ORG",
      sub_title: organization.sub_title || "",
      category_id: organization.category_id || (organization.org_type === "EDU" ? 1 : 2),
      employee_count_min: organization.employee_count_min?.toString() || "",
      employee_count_max: organization.employee_count_max?.toString() || "",
      address: organization.address || "",
      website_url: organization.website_url || "",
      ph_no: extractPhoneDigits(organization.ph_no),
      linkedin_url: organization.linkedin_url || "",
      logo_url: organization.logo_url || "",
      banner_url: organization.banner_url || "",
    });
    
    // Update upload form with existing values
    if (organization.logo_url) {
      uploadFormMethods.setValue("logo_url", {
        value: organization.logo_url,
        previewUrl: organization.logo_url.startsWith("http") ? organization.logo_url : "",
      });
    }
    if (organization.banner_url) {
      uploadFormMethods.setValue("banner_url", {
        value: organization.banner_url,
        previewUrl: organization.banner_url.startsWith("http") ? organization.banner_url : "",
      });
    }
  }, [organization, uploadFormMethods]);

  // Generate slug from name
  const generateSlug = (name: string): string => {
    return name
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "") // Remove special characters
      .replace(/\s+/g, "-") // Replace spaces with hyphens
      .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    
    // Auto-set category_id based on org_type
    if (name === "org_type") {
      const categoryId = value === "ORG" ? 2 : value === "EDU" ? 1 : 2; // Default to 2 if invalid
      setFormData((prev) => ({ ...prev, [name]: value, category_id: categoryId }));
    } else if (name === "name") {
      // Auto-generate slug from name
      const generatedSlug = generateSlug(value);
      setFormData((prev) => ({ ...prev, name: value, slug: generatedSlug }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // Only allow numbers
    if (value === "" || /^\d+$/.test(value)) {
      setFormData((prev) => ({ ...prev, [name]: value }));
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }
    if (!formData.slug.trim()) {
      newErrors.slug = "Slug is required";
    } else if (!/^[a-z0-9-]+$/.test(formData.slug)) {
      newErrors.slug = "Slug must contain only lowercase letters, numbers, and hyphens";
    }
    if (formData.website_url && !/^https?:\/\/.+/.test(formData.website_url)) {
      newErrors.website_url = "Website URL must be a valid URL (starting with http:// or https://)";
    }
    if (formData.linkedin_url && !/^https?:\/\/.+/.test(formData.linkedin_url)) {
      newErrors.linkedin_url = "LinkedIn URL must be a valid URL (starting with http:// or https://)";
    }
    if (formData.employee_count_min && formData.employee_count_max) {
      const min = parseInt(formData.employee_count_min);
      const max = parseInt(formData.employee_count_max);
      if (min > max) {
        newErrors.employee_count_max = "Maximum must be greater than or equal to minimum";
      }
    }
    if (!formData.ph_no.trim()) {
      newErrors.ph_no = "Phone number is required";
    } else if (!/^\d+$/.test(formData.ph_no.trim())) {
      newErrors.ph_no = "Phone number must contain only numbers";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!validate()) return;

    try {
      // Get uploaded image values from form
      const logoValue = uploadFormMethods.getValues("logo_url");
      const bannerValue = uploadFormMethods.getValues("banner_url");
      
      const sanitizeText = (value?: string) => {
        const trimmed = value?.trim() || "";
        return trimmed.length > 0 ? trimmed : '';
      };
      const employeeCountMin = formData.employee_count_min
        ? parseInt(formData.employee_count_min, 10)
        : 0;
      const employeeCountMax = formData.employee_count_max
        ? parseInt(formData.employee_count_max, 10)
        : 0;

      const payload: any = {
        name: formData.name.trim(),
        slug: formData.slug.trim(),
        org_type: formData.org_type,
        description: sanitizeText(formData.description),
        sub_title: sanitizeText(formData.sub_title),
        category_id: formData.category_id,
        employee_count_min: Number.isNaN(employeeCountMin) ? null : employeeCountMin,
        employee_count_max: Number.isNaN(employeeCountMax) ? null : employeeCountMax,
        address: sanitizeText(formData.address),
        website_url: sanitizeText(formData.website_url),
        ph_no: sanitizeText(formData.ph_no),
        linkedin_url: sanitizeText(formData.linkedin_url),
        logo_url: logoValue?.value || '',
        banner_url: bannerValue?.value || '',
      };

      const result = await updateMutation.mutateAsync({ id: organization.id, ...payload });
      if (result.status === 200 || result.status === 201) {
        toastSuccessMessage(result.data?.message || "Organization updated successfully");
        afterCall?.();
        onClose();
      } else {
        toastErrorMessage(result.data?.message || "Failed to update organization");
      }
    } catch (error: any) {
      const msg = error?.response?.data?.message || error.message || "Failed to update organization";
      toastErrorMessage(msg);
    }
  };

  return (
    <div>
    <form onSubmit={handleSubmit} className="flex flex-col gap-4 p-4 max-h-[calc(100vh-120px)] overflow-y-auto">
      {/* Basic Information */}
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-text-default border-b pb-2">Basic Information</h3>
        
        <div className="flex flex-col gap-2">
          <label htmlFor="name" className="text-sm font-medium text-text-default">
            Name <span className="text-[red]">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`px-3 py-2 border rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary ${
              errors.name ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="Organization Name"
          />
          {errors.name && <span className="text-xs !text-[red] font-medium">{errors.name}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="slug" className="text-sm font-medium text-text-default">
            Url <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center border rounded-md overflow-hidden">
            <div className="px-3 py-2 bg-gray-50 text-text-default text-13 border-r whitespace-nowrap">
              {LP_FE_BASE_URL}/{formData.org_type?.toLowerCase() || "org"}/
            </div>
            <input
              type="text"
              id="slug"
              name="slug"
              value={formData.slug}
              onChange={handleChange}
              className={`flex-1 px-3 py-2 text-13 outline-none focus:ring-1 focus:ring-text-primary ${
                errors.slug ? "border-red-500" : ""
              }`}
              placeholder="organization-slug"
            />
          </div>
          <div className="text-xs text-text-default">
            Full URL: <span className="font-mono">{LP_FE_BASE_URL}/{formData.org_type?.toLowerCase() || "org"}/{formData.slug || "organization-slug"}</span>
          </div>
          {errors.slug && <span className="text-xs text-[red]">{errors.slug}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="sub_title" className="text-sm font-medium text-text-default">
            Sub Title
          </label>
          <input
            type="text"
            id="sub_title"
            name="sub_title"
            value={formData.sub_title}
            onChange={handleChange}
            maxLength={200}
            className="px-3 py-2 border border-gray-300 rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary"
            placeholder="Organization Sub Title"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="org_type" className="text-sm font-medium text-text-default">
            Organization Type
          </label>
          <select
            id="org_type"
            name="org_type"
            value={formData.org_type}
            onChange={handleChange}
            className="px-3 py-2 border border-gray-300 rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary"
          >
            <option value="ORG">ORG</option>
            <option value="EDU">EDU</option>
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="description" className="text-sm font-medium text-text-default">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={3}
            className="px-3 py-2 border border-gray-300 rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary resize-none"
            placeholder="Organization Description"
          />
        </div>
      </div>

      {/* Employee Count */}
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-text-default border-b pb-2">Size</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label htmlFor="employee_count_min" className="text-sm font-medium text-text-default">
              Employee Count (Min)
            </label>
            <input
              type="text"
              id="employee_count_min"
              name="employee_count_min"
              value={formData.employee_count_min}
              onChange={handleNumberChange}
              className="px-3 py-2 border border-gray-300 rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary"
              placeholder="e.g., 100"
            />
          </div>

          <div className="flex flex-col gap-2">
            <label htmlFor="employee_count_max" className="text-sm font-medium text-text-default">
              Employee Count (Max)
            </label>
            <input
              type="text"
              id="employee_count_max"
              name="employee_count_max"
              value={formData.employee_count_max}
              onChange={handleNumberChange}
              className={`px-3 py-2 border rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary ${
                errors.employee_count_max ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="e.g., 300"
            />
            {errors.employee_count_max && <span className="text-xs text-[red]">{errors.employee_count_max}</span>}
          </div>
        </div>
      </div>

      {/* Location & Links */}
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-text-default border-b pb-2">Location & Links</h3>
        
        <div className="flex flex-col gap-2">
          <label htmlFor="address" className="text-sm font-medium text-text-default">
            Address
          </label>
          <textarea
            id="address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            rows={2}
            className="px-3 py-2 border border-gray-300 rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary resize-none"
            placeholder="Organization Address"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="website_url" className="text-sm font-medium text-text-default">
            Website URL
          </label>
          <input
            type="url"
            id="website_url"
            name="website_url"
            value={formData.website_url}
            onChange={handleChange}
            className={`px-3 py-2 border rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary ${
              errors.website_url ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="https://www.example.com"
          />
          {errors.website_url && <span className="text-xs text-[red]">{errors.website_url}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="ph_no" className="text-sm font-medium text-text-default">
            Phone Number <span className="text-[red]">*</span>
          </label>
          <input
            type="text"
            id="ph_no"
            name="ph_no"
            value={formData.ph_no}
            onChange={handleNumberChange}
            className={`px-3 py-2 border rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary ${
              errors.ph_no ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="e.g., 1234567890"
          />
          {errors.ph_no && <span className="text-xs text-[red]">{errors.ph_no}</span>}
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="linkedin_url" className="text-sm font-medium text-text-default">
            LinkedIn URL
          </label>
          <input
            type="url"
            id="linkedin_url"
            name="linkedin_url"
            value={formData.linkedin_url}
            onChange={handleChange}
            className={`px-3 py-2 border rounded-md text-13 outline-none focus:ring-1 focus:ring-text-primary ${
              errors.linkedin_url ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="https://www.linkedin.com/company/example"
          />
          {errors.linkedin_url && <span className="text-xs text-[red]">{errors.linkedin_url}</span>}
        </div>
      </div>

      {/* Media */}
      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-text-default border-b pb-2">Media</h3>
        
        <FormProvider {...uploadFormMethods}>
        <label htmlFor="logo_url" className="text-sm font-medium text-text-default">
            Logo URL
          </label>
          <UploadFileInput
            label="Logo"
            field="logo_url"
            uploadKey="organization-logo"
            validation={{ required: false, allowedFileType: "image" }}
            message={{
              success: "Logo uploaded successfully!",
              error: "Failed to upload logo",
            }}
            isFreeCrop={true}
          />
          
          <label htmlFor="logo_url" className="text-sm font-medium text-text-default">
            Banner URL
          </label>
          <UploadFileInput
            label="Banner"
            field="banner_url"
            uploadKey="organization-banner"
            validation={{ required: false, allowedFileType: "image" }}
            message={{
              success: "Banner uploaded successfully!",
              error: "Failed to upload banner",
            }}
            isFreeCrop={true}
          />
        </FormProvider>
      </div>


    </form>
          <div className="flex justify-end gap-2 p-[16px] border-t sticky bottom-0 bg-white">
          <Button type="button" variant="outline" onClick={onClose} disabled={updateMutation.isPending}>
            Cancel
          </Button>
          <Button type="button" variant="default" onClick={handleSubmit} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? (
              <Center className="gap-2">
                Updating...
              </Center>
            ) : (
              "Update"
            )}
          </Button>
        </div>
        </div>
  );
};

export default UpdateOrganizationModal;
